import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [showUserInfoModal, setShowUserInfoModal] = useState(false);
    const [pendingCartItem, setPendingCartItem] = useState(null); // Store item to add after user info
    const [wishlist, setWishlist] = useState([]);
    const [cart, setCart] = useState([]);

    // Load user info and data from localStorage on mount
    useEffect(() => {
        const savedUser = localStorage.getItem('user_info');
        const savedWishlist = localStorage.getItem('guest_wishlist');
        const savedCart = localStorage.getItem('guest_cart');

        if (savedUser) {
            setUser(JSON.parse(savedUser));
        }
        // Don't show modal on page load anymore

        if (savedWishlist) setWishlist(JSON.parse(savedWishlist));
        if (savedCart) setCart(JSON.parse(savedCart));
    }, []);

    // Save user info
    const saveUserInfo = (name, email) => {
        const userInfo = {
            name,
            email,
            avatar: null
        };
        setUser(userInfo);
        localStorage.setItem('user_info', JSON.stringify(userInfo));
        setShowUserInfoModal(false);

        // Add pending cart item if exists
        if (pendingCartItem) {
            const existingItem = cart.find(cartItem => cartItem.id === pendingCartItem.id);
            let updatedCart;
            
            if (existingItem) {
                updatedCart = cart.map(cartItem =>
                    cartItem.id === pendingCartItem.id
                        ? { ...cartItem, quantity: cartItem.quantity + (pendingCartItem.quantity || 1) }
                        : cartItem
                );
            } else {
                updatedCart = [...cart, { ...pendingCartItem, quantity: pendingCartItem.quantity || 1 }];
            }
            
            setCart(updatedCart);
            localStorage.setItem('guest_cart', JSON.stringify(updatedCart));
            setPendingCartItem(null);
        }
    };

    // Update user info
    const updateUserInfo = (name, email) => {
        const userInfo = {
            ...user,
            name,
            email
        };
        setUser(userInfo);
        localStorage.setItem('user_info', JSON.stringify(userInfo));
    };

    const addToWishlist = (item) => {
        const updatedWishlist = [...wishlist, item];
        setWishlist(updatedWishlist);
        localStorage.setItem('guest_wishlist', JSON.stringify(updatedWishlist));
    };

    const removeFromWishlist = (itemId) => {
        const updatedWishlist = wishlist.filter(item => item.id !== itemId);
        setWishlist(updatedWishlist);
        localStorage.setItem('guest_wishlist', JSON.stringify(updatedWishlist));
    };

    const isInWishlist = (itemId) => {
        return wishlist.some(item => item.id === itemId);
    };

    const addToCart = (item) => {
        // Show modal if user hasn't provided info yet (only on first cart add)
        const savedUser = localStorage.getItem('user_info');
        if (!savedUser && cart.length === 0) {
            setPendingCartItem(item); // Store the item to add after user fills info
            setShowUserInfoModal(true);
            return; // Don't add to cart yet, user needs to fill info first
        }

        const existingItem = cart.find(cartItem => cartItem.id === item.id);
        let updatedCart;
        
        if (existingItem) {
            updatedCart = cart.map(cartItem =>
                cartItem.id === item.id
                    ? { ...cartItem, quantity: cartItem.quantity + (item.quantity || 1) }
                    : cartItem
            );
        } else {
            updatedCart = [...cart, { ...item, quantity: item.quantity || 1 }];
        }
        
        setCart(updatedCart);
        localStorage.setItem('guest_cart', JSON.stringify(updatedCart));
    };

    const updateCartQuantity = (itemId, newQuantity) => {
        if (newQuantity < 1) return;
        
        const updatedCart = cart.map(item =>
            item.id === itemId
                ? { ...item, quantity: newQuantity }
                : item
        );
        
        setCart(updatedCart);
        localStorage.setItem('guest_cart', JSON.stringify(updatedCart));
    };

    const removeFromCart = (itemId) => {
        const updatedCart = cart.filter(item => item.id !== itemId);
        setCart(updatedCart);
        localStorage.setItem('guest_cart', JSON.stringify(updatedCart));
    };

    const value = {
        user,
        saveUserInfo,
        updateUserInfo,
        showUserInfoModal,
        setShowUserInfoModal,
        wishlist,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        cart,
        addToCart,
        updateCartQuantity,
        removeFromCart
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
